import type { ComponentType } from 'react';
import React, { useCallback, useContext, useMemo, useState } from 'react';

type EditorRevertDialogContextType = {
	shouldShowRevertDialog: boolean;
	shouldShowRevertDialogDiff: boolean;
	hideRevertDialogDiff: () => void;
	showRevertDialogDiff: () => void;
	hideRevertDialog: () => void;
	showRevertDialog: () => void;
};

const EditorRevertDialogContext = React.createContext<EditorRevertDialogContextType | undefined>(
	undefined,
);

type EditorRevertDialogContextProviderProps = {
	children: React.ReactNode;
};
export const EditorRevertDialogContextProvider = ({
	children,
}: EditorRevertDialogContextProviderProps) => {
	const [shouldShowRevertDialog, setShouldShowRevertDialog] = useState(false);
	const [shouldShowRevertDialogDiff, setShouldShowRevertDialogDiff] = useState(false);

	const hideRevertDialogDiff = useCallback(() => setShouldShowRevertDialogDiff(false), []);
	const showRevertDialogDiff = useCallback(() => setShouldShowRevertDialogDiff(true), []);
	const hideRevertDialog = useCallback(() => setShouldShowRevertDialog(false), []);
	const showRevertDialog = useCallback(() => {
		setShouldShowRevertDialog(true);
		setShouldShowRevertDialogDiff(false);
	}, []);

	const editorRevertDialogContextValue = useMemo(
		() => ({
			shouldShowRevertDialog,
			shouldShowRevertDialogDiff,
			hideRevertDialogDiff,
			showRevertDialogDiff,
			hideRevertDialog,
			showRevertDialog,
		}),
		[
			shouldShowRevertDialog,
			shouldShowRevertDialogDiff,
			hideRevertDialogDiff,
			showRevertDialogDiff,
			hideRevertDialog,
			showRevertDialog,
		],
	);

	return (
		<EditorRevertDialogContext.Provider value={editorRevertDialogContextValue}>
			{children}
		</EditorRevertDialogContext.Provider>
	);
};

export const useEditorRevertDialogContext = () => {
	const context = useContext(EditorRevertDialogContext);

	if (context === undefined) {
		throw new Error(
			'EditorRevertDialogContext must be used within an EditorRevertDialogContextProvider',
		);
	}

	return context;
};

export function withRevertContext<T>(WrappedComponent: ComponentType<T>) {
	return (props) => (
		<EditorRevertDialogContextProvider>
			<WrappedComponent {...props} />
		</EditorRevertDialogContextProvider>
	);
}
